import { Button, Icon } from '@genial/components';
import { Typography } from '@genial/design-tokens';
import React, { useCallback, useState } from 'react';

import { useReportGeneratorActions } from '../../../../../store/reportGenerator/actions';
import { ReportGeneratorListItemNormalized } from '../../../../../store/reportGenerator/types';
import { reportGeneratorTheme } from '../../../constants/theme';
import { ReportGeneratorRetryLimitModal } from '../../shared/ReportGeneratorRetryLimitModal';

interface DetailsModalFooterProps {
  item: ReportGeneratorListItemNormalized;
  onActionComplete?: () => void;
}

const detailsButtonStyle = {
  bg: reportGeneratorTheme.details.buttonBg,
  h: 'auto',
  minH: 'auto',
  py: '14px',
  _text: {
    color: 'neutral.white',
    textTransform: 'none' as const,
    fontSize: Typography['fontsize.md'],
  },
  _stack: { space: '12px' },
  _hover: { bg: 'brand.primary.dark' },
  _disabled: {
    bg: reportGeneratorTheme.details.buttonDisabledBg,
    opacity: 1,
  },
};

export const DetailsModalFooter: React.FC<DetailsModalFooterProps> = ({
  item,
  onActionComplete,
}) => {
  const { getReportingFileAndDownload, retryReportingFile, resetPage } =
    useReportGeneratorActions();
  const [actionLoading, setActionLoading] = useState(false);
  const [isRetryLimitOpen, setIsRetryLimitOpen] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!item.isDownloadEnabled || actionLoading) {
      return;
    }

    setActionLoading(true);
    await getReportingFileAndDownload(item);
    setActionLoading(false);
    onActionComplete?.();
  }, [actionLoading, getReportingFileAndDownload, item, onActionComplete]);

  const handleRetry = useCallback(async () => {
    if (actionLoading) {
      return;
    }

    if (!item.isAbleToRetry) {
      setIsRetryLimitOpen(true);
      return;
    }

    setActionLoading(true);
    await retryReportingFile(item);
    resetPage();
    setActionLoading(false);
    onActionComplete?.();
  }, [actionLoading, item, onActionComplete, resetPage, retryReportingFile]);

  if (item.isFileWithError) {
    return (
      <>
        <Button
          w="full"
          testID="report-generator-details-retry"
          variant="primary"
          onPress={handleRetry}
          isLoading={actionLoading}
          isDisabled={actionLoading}
          leftIcon={
            <Icon
              name="RefreshAlt"
              size="xxxs"
              color="neutral.white"
              testId="report-generator-details-retry-icon"
            />
          }
          {...detailsButtonStyle}>
          Tentar novamente
        </Button>

        <ReportGeneratorRetryLimitModal
          isOpen={isRetryLimitOpen}
          onClose={() => setIsRetryLimitOpen(false)}
        />
      </>
    );
  }

  return (
    <Button
      w="full"
      testID="report-generator-details-download"
      variant="primary"
      onPress={handleDownload}
      isLoading={actionLoading}
      isDisabled={!item.isDownloadEnabled || actionLoading}
      leftIcon={
        <Icon
          name="Download"
          size="xxxs"
          color="neutral.white"
          testId="report-generator-details-download-icon"
        />
      }
      {...detailsButtonStyle}>
      Fazer download
    </Button>
  );
};
